<template>
  <div
    class="cart-item__row"
    v-bind:class="{'fullcart-item' :options.displayType =='cart'}"
  >
    <!-- Delete confirmation flyout -->
    <div
      class="cart-item__delete-confirm"
      v-show="isDeleting"
      v-bind:class="{ 'isDeleting' : isDeleting }"
    >
      <div class="cart-item__delete-confirm-message">Are you sure you want to remove?</div>
      <div class="d-flex">
        <button
          class="btn btn-secondary mr-3"
          @click="toggleDelete"
          ref="removeItemCancel"
          style="border: 1px solid #666"
        >Cancel</button>
        <button
          class="btn btn-primary"
          @click="deleteItem(item.id)"
          v-on:keyup.esc="toggleDelete"
        >Remove</button>
      </div>
    </div>

    <div
      class="cart-item__row-product"
      v-bind:class="{ 'isDeleting disabled' : isDeleting, 'disabled' : disabled }"
    >
      <div class="cart-item__delete" v-show="!isDeleting">
        <a
          href="#"
          @click.prevent="toggleDelete"
          class="fa fa-times-circle cart-delete-item"
          :aria-label="'Remove' + item.product.displayName"
        ></a>
      </div>

      <div class="cart-item__thumbnail-container">
        <a :href="this.pdpUrl" @click.prevent="handleProductLink" aria-hidden="true" tabindex="-1">
          <img v-bind:src="item.sku.thumbnailImageUrl" class="cart-item__thumbnail" alt />
        </a>
      </div>

      <div class="cart-item__display-name-and-note">
        <img v-if="item.product.isBestAvailable" src="/img/flag-best-available.svg" class="cart-item__flag-best-available"  alt="Best available">
        <a :href="this.pdpUrl" @click.prevent="handleProductLink" class="cart-item__display-name">
          <span v-html="item.product.displayName"></span>
          <span v-if="item.sku.customerFriendlySize">, {{item.sku.customerFriendlySize}}</span>
        </a>
        <div class="cart-item__preference-and-note">
          <span
            v-if="item.purchasePreference && item.purchasePreference.text"
            class="cart-item__preference">
            {{ item.purchasePreference.text }}
          </span>
          <cart-item-note
            :bucket="bucket"
            :cartItem="item"
            :cartItemIndex="itemIndex"
            class="d-none d-lg-block cart-item__note"
          ></cart-item-note>
        </div>
      </div>

      <!-- Note and Substitutions shown for mobile only -->
      <div
        class="cart-item__note-sub-mobile d-lg-none"
      >
        <cart-item-note :bucket="bucket" :cartItem="item" class="cart-item__note"></cart-item-note>
        <div class="cart-item__substitution" v-if="item.substitutionPreferenceEnabled">
          <input :id="`subs-preference-${item.id}-mobile`" class="cart-item__substitution-checkbox"
            type="checkbox"
            v-model="item.allowSubstitution"
            @change="toggleItemSubstitution"
          />
          <label :for="`subs-preference-${item.id}-mobile`" class="cart-item__substitution-label">
            Allow subs
          </label>
        </div>
      </div>
      <!-- End Note and Substitutions shown for mobile only -->

      <!-- Substitutions shown for desktop only -->
      <div class="cart-item__substitution d-none d-lg-flex" v-if="item.substitutionPreferenceEnabled">
        <input :id="`subs-preference-${item.id}`" class="cart-item__substitution-checkbox"
          type="checkbox"
          v-model="item.allowSubstitution"
          @change="toggleItemSubstitution"
        />
        <label :for="`subs-preference-${item.id}`" class="cart-item__substitution-label">
          Allow subs
        </label>
      </div>

      <!-- Show either the quantity paddles or weight selector -->
      <div class="cart-item__quantity" v-if="!cartItem.product.pricedByWeight" ref="paddles">
        <div
          class="cart-item__quantity-placeholder btn-group"
          v-bind:class="{'paddle-dropshadow' : paddlesActive && isSmallDisplay}"
        >
          <button @click="decrement" class="btn btn-decrement" v-show="paddlesActive">-</button>
          <input
            type="number"
            v-bind:value="item.quantity"
            @blur="checkNullThenCheckQuantity"
            @keyup="checkQuantity"
            @submit="checkQuantity"
            class="btn cart-item__input-quantity"
            v-bind:id="item.id"
            @click.stop.prevent="showThePaddles"
            v-bind:class="{'notActive' : !paddlesActive}"
          />

          <button
            @click="increment"
            class="btn btn-increment"
            v-show="paddlesActive"
            :class="overrideAddToBucket ? 'disabled' : ''"
          >+</button>
        </div>
      </div>

      <div class="cart-item__quantity" v-if="cartItem.product.pricedByWeight">
        <div
          class="btn cart-item__input-quantity cart-item__estimated-weight"
          @click.stop.prevent="showThePaddles"
          v-show="!paddlesActive && isSmallDisplay"
        >{{ cartItem.estimatedWeight }} {{ cartItem.sku.priceInfo.listPrice.unit }}</div>

        <select
          @change="updateWeight"
          @focus="setPreviousWeight"
          v-model="cartItem.estimatedWeight"
          v-show="paddlesActive"
          ref="weightAdjustments"
        >
          <option disabled="disabled">Select a Weight</option>
          <option
            v-for="(weight, index) in cartItem.sku.weightSelectionIncrements"
            v-if="cartItem.sku.weightSelectionIncrements"
            v-bind:key="'weightOption'+index"
            v-bind:value="weight"
          >{{weight}} {{ cartItem.sku.priceInfo.listPrice.unit }}</option>
        </select>
      </div>

      <div class="cart-item__adjustments-toggle-container">
        <div
          v-if="item.priceInfo.adjustments.length > 0 || item.priceInfo.saleDiscount"
          class="cart-item__adjustments-toggle"
        >
          <span
            class="fa fa-angle-up cart-item__adjustment-circle"
            @click="toggleShowAdjustments"
            v-show="showAdjustments"
          ></span>
          <span
            class="fa fa-angle-down cart-item__adjustment-circle"
            @click="toggleShowAdjustments"
            v-show="!showAdjustments"
          ></span>
        </div>
      </div>

      <div class="cart-item__price-container">
        <div v-if="item.priceInfo.isDiscounted" class="cart-item__sale-price">
          <!-- on sale! -->
          {{item.priceInfo.totalAmount.formattedAmount}}
        </div>

        <div
          class="cart-item__total-price"
          v-bind:class="{ 'cart-item__total-price-disabled': item.priceInfo.isDiscounted }"
        >
          {{`${item.priceInfo.rawTotalPrice.formattedAmount}${item.product.isBestAvailable ? '*' : ''}`}}
        </div>


        <div class="cart-item__each-price" v-if="item.product.isBestAvailable">
          {{
            item.quantity > 1
              ? `${item.sku.priceInfo.listPrice.formatted}/${item.sku.priceInfo.listPrice.unit}`
              : item.sku.priceInfo.listPrice.formatted
          }}
        </div>
        <div class="cart-item__each-price" v-else-if="item.quantity > 1 || item.product.pricedByWeight">
          <span v-if="item.sku.onSale">
            {{item.sku.priceInfo.salePrice.formatted}}/{{item.sku.priceInfo.salePrice.unit}}
          </span>
          <span v-else>
            {{item.sku.priceInfo.listPrice.formatted}}/{{item.sku.priceInfo.listPrice.unit}}
          </span>
        </div>
      </div>

      <!-- user is deleting or updating quantity on mobile -->
      <div class="cart-item__row__disabled-overlay"></div>
    </div>

    <div class="cart-item__error-bar" v-show="cartItemErrorMessage" role="alert">
      <div class="alert mb-2 d-flex alert-secondary">
        <div data-message-type="error" v-html="cartItemErrorMessage"></div>
      </div>
    </div>

    <div class="cart-item__bottom-bar">
      <section>
        <!-- A promotion that has a Closeness Qualifier set up -->
        <div
          v-for="(promotion, index) in item.availablePromotions"
          v-bind:key="'promoIndex' + index"
          v-bind:class="isSmallDisplay ? '' : 'd-flex'"
          v-if="promotion && showCoupons"
        >
          <div v-if="promotion.closenessQualifiers.length">
            <closeness-qualifier
              v-bind:cartItem="item"
              v-bind:cq="cq"
              v-bind:coupon="promotion"
              v-for="(cq, index) in promotion.closenessQualifiers"
              v-bind:key="'cq'+index"
              v-bind:description="true"
              v-bind:itemIndex="itemIndex"
            ></closeness-qualifier>
          </div>
        </div>

        <!-- A coupon that has been applied and has a Closeness Qualifier set up -->
        <div
          v-for="(coupon, index) in item.availableCoupons"
          v-bind:key="'couponIndex'+index"
          v-bind:class="isSmallDisplay ? '' : 'd-flex'"
          v-if="coupon && showCoupons"
        >
          <!-- Theoretically an offer could have multiple closenessQualifiers,
                  for example, 10% off 10 items, 15% off 15 items.
          this is not supported for the initial curbside release in 2019-->

          <template>
            <!-- A coupon with a Closeness Qualifier -->
            <div v-if="coupon.closenessQualifiers.length">
              <closeness-qualifier
                v-bind:cartItem="item"
                v-bind:cq="cq"
                v-bind:coupon="coupon"
                v-for="(cq, index) in coupon.closenessQualifiers"
                v-bind:key="'cq'+index"
                v-bind:description="true"
                v-bind:itemIndex="itemIndex"
              ></closeness-qualifier>
            </div>
            <div v-else>
              <!-- A coupon that has NOT been clipped but will apply to the product and does NOT have a closeness Qualifier set up -->
              <closeness-qualifier
                v-bind:cartItem="item"
                v-bind:coupon="coupon"
                v-bind:cq="{percentComplete: 1, callToAction: 'Clip Coupon to Qualify', name: coupon.displayName}"
                v-bind:description="true"
                v-bind:itemIndex="itemIndex"
              ></closeness-qualifier>
            </div>
          </template>

          <div v-show="coupon.unclipped">
            <button class="btn btn-coupon" @click="clipCoupon(coupon)">
              <i class="fa fa-scissors" aria-hidden="true"></i> Clip
            </button>
          </div>
        </div>

        <!-- A coupon that has successfully been applied AND does NOT have a closeness Qualifier set up (it will not appear in availableCoupons array -->
        <div
          v-if="item.priceInfo.adjustments && showCoupons"
          v-for="(adjustment, index) in item.priceInfo.adjustments"
          v-bind:key="'adjustment'+index"
        >
          <closeness-qualifier
            v-if="adjustment.promotion.couponId"
            v-bind:cartItem="item"
            v-bind:description="true"
            v-bind:cq="{
              percentComplete: 1,
              callToAction: 'Offer Completed',
              name: adjustment.displayName
            }"
            v-bind:coupon="{
              displayName: adjustment.displayName,
              couponId: adjustment.promotion.couponId,
              unclipped: false
            }"
            v-bind:itemIndex="itemIndex"
          ></closeness-qualifier>
        </div>
      </section>

      <table class="cart-item__adjustments" v-show="showAdjustments">
        <!-- Sale adjustment -->
        <tr v-if="item.priceInfo.saleDiscount">
          <td class="cart-item__adjustment-type">Sale</td>
          <td
            class="cart-item__adjustment-value"
          >&#8209;{{item.priceInfo.saleDiscount.formattedAmount}}</td>
        </tr>

        <!-- All other adjustments -->
        <tr
          v-for="(adjustment, index) in item.priceInfo.adjustments"
          v-if="item.priceInfo.adjustments"
          v-bind:key="index"
        >
          <td class="cart-item__adjustment-type" v-if="adjustment.promotion.couponId">Coupon Savings</td>
          <td
            class="cart-item__adjustment-type"
            v-if="!adjustment.promotion.couponId"
          >{{adjustment.displayName}}</td>
          <td
            class="cart-item__adjustment-value"
            v-bind:title="adjustment.displayName"
          >&#8209;{{adjustment.totalPromotionAdjustment.formattedAmount}}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
// Pass in `cartItem` aka $store.state.cart.commerceItems.[categoryid][item]
import CartItemNote from './CartItemNote'
import ClosenessQualifier from '../ClosenessQualifier'
import CouponDetailModal from '../CouponDetailModal'
import errorMessaging from '../mixins/errorMessaging';
import { PRODUCT_CLICK_EVENT } from "../../utilities/constants";
import Vue from 'vue'

export default {
  name: "CartItem",
  components: {
    "cart-item-note": CartItemNote,
    "closeness-qualifier": ClosenessQualifier,
    "coupon-detail-modal": CouponDetailModal
    // 'coupon-qualifier': CouponQualifier
  },
  mixins: [errorMessaging],
  props: {
    listname: {
      type: String,
      default: ""
    },
    reco: {
      type: String,
      default: "default"
    },
    position: {
      type: Number,
      default: 1
    },
    cartItem: {
      type: Object
    },
    categoryIndex: {
      type: Number
    },
    itemIndex: {
      type: Number
    },
    bucket: {
      type: String,
      default: "cart" // cart, orderAddOns. Used for figuring out how many items are in a specific bucket
    },
    productLinkDestination: {
      type: String,
      default: "link" // link, modal. A switch that choose how to handle the destination of clicking on a link in the product card
    },
    overrideAddToBucket: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => ({
        disableLinks: false,
        displayType: "cart"
      })
    },
    showCoupons: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      disabled: false,
      isDeleting: false,
      showAdjustments: false,
      paddlesActive: false,
      selected: "",
      previousWeight: 0,
      couponModalShown: false,
      coupon: {},
      item: Vue.util.extend(this.cartItem), //local copy for doing frontend modifications that will later be ajaxed
      pendingResponse: false,
      cartItemErrorMessage: false
    }
  },
  computed: {
    maximumOrderLimitReached(){
      return this.item.quantity >= _.get(this, 'cartItem.product.maximumOrderQuantity', this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY)
    },
    pdpUrl: function () {
      if (this.options.disableLinks && this.item.product.pdpUrl) {
        return "#";
      } else {
        return this.item.product.pdpUrl;
      }
    }
  },
  methods: {
    getParents(elem, selector) {
      // Does the same thing as jQuery .parents()
      // Element.matches() polyfill
      if (!Element.prototype.matches) {
        Element.prototype.matches =
          Element.prototype.matchesSelector ||
          Element.prototype.mozMatchesSelector ||
          Element.prototype.msMatchesSelector ||
          Element.prototype.oMatchesSelector ||
          Element.prototype.webkitMatchesSelector ||
          function(s) {
            var matches = (
                this.document || this.ownerDocument
              ).querySelectorAll(s),
              i = matches.length;
            while (--i >= 0 && matches.item(i) !== this) {}
            return i > -1;
          };
      }

      // Setup parents array
      var parents = [];

      // Get matching parent elements
      for (; elem && elem !== document; elem = elem.parentNode) {
        // Add matching parents to array
        if (selector) {
          if (elem.matches(selector)) {
            parents.push(elem);
          }
        } else {
          parents.push(elem);
        }
      }

      return parents;
    },
    handleProductLink() {
      switch (this.productLinkDestination) {
        case "link":
          this.gtmClick(
            PRODUCT_CLICK_EVENT,
            this.item,
            this.bucket,
            this.listname,
            "",
            "cart",
            this.position,
            this.reco
          );
          window.location = this.pdpUrl;
          break;
        case "modal":
          this.gtmClick(
            PRODUCT_CLICK_EVENT,
            this.item,
            this.bucket,
            this.listname,
            "",
            "cart",
            this.position,
            this.reco
          );
          this.gtmClick(
            "e_productDetailView",
            this.item,
            this.bucket,
            this.listname,
            "",
            "cart",
            this.position,
            this.reco
          );
          this.hupOpenModal({
            modalId: "product-detail",
            productId: this.cartItem.product.id,
            bucket: this.bucket
          });

          // this.$store.commit('profile/OPEN_MODAL', {
          //   id: 'product-detail',
          //   shown: true,
          //   options: {
          //     productId: this.cartItem.product.id,
          //     bucket: this.bucket
          //   }
          // })
          break;
      }
    },
    loadCouponModal() {
      this.couponModalShown = true;
    },
    setTip: function() {
      this.$store.dispatch("cart/POST_TIP_AMOUNT", this.tipAmount);
    },
    toggleDelete() {
      this.isDeleting = !this.isDeleting;
      this.$nextTick(() => {
        // set the next tab position
        this.$refs.removeItemCancel.focus();
        this.$refs.removeItemCancel.blur();
      });
    },
    toggleShowAdjustments() {
      this.showAdjustments = !this.showAdjustments;
    },
    setPreviousWeight: function (e) {
      this.previousWeight = e.target.value
    },
    updateWeight: function(e) {
      // Weight uses the add to cart endpoint. Quantity is always 1.

      if (e.target.value == 0) {
        this.toggleDelete();

        // reset value back to original
        e.target.value = this.previousWeight;
        this.cartItem.estimatedWeight = this.previousWeight;

        return;
      }

      var item = {
        productId: this.cartItem.product.id,
        skuId: this.cartItem.sku.id,
        quantity: 1,
        weight: e.target.value * 1
      };

      if (this.cartItem.purchasePreference) {
        item.purchasePreferenceId = this.cartItem.purchasePreference.preferenceId;
      }
      this.$store.dispatch(this.bucket + "/ADD_ITEM_TO_BUCKET", item);
      if (this.isSmallDisplay) {
        this.disabled = false;
        this.paddlesActive = false;
      }
    },
    showThePaddles: function() {
      this.paddlesActive = true;
      if (this.isSmallDisplay) {
        this.disabled = true; //white out the background

        if (this.cartItem.product.pricedByWeight) {
          // Listeners for weighted
          this.$nextTick(function() {
            // DOM updated
            this.$refs.weightAdjustments.focus();
            document.addEventListener("click", this.hideThePaddles, true);
          });
        } else {
          document.addEventListener("click", this.hideThePaddles, true);
        }
      }
    },
    hideThePaddles: function(e) {
      var clickedSomewhereInThePaddle = this.getParents(
        e.target,
        ".cart-item__quantity"
      );

      if (clickedSomewhereInThePaddle.length > 0) {
        return;
      } else {
        this.paddlesActive = false;
        this.disabled = false;
        document.removeEventListener("click", this.hideThePaddles, true);
      }
    },
    deleteItem: function(id) {
      this.toggleDelete();
      this.$store.dispatch(this.bucket + "/DELETE_ITEM", id);
    },
    toggleItemSubstitution() {
      this.$store.commit(this.bucket + "/TOGGLE_ITEM_SUBSTITUTION", {
        itemIndex: this.itemIndex,
        categoryIndex: this.categoryIndex
      });
    },
    checkNullThenCheckQuantity(e) {
      if (e.target.value == "") {
        // reset value back to original
        e.target.value = this.cartItem.quantity;
        this.item.quantity = this.cartItem.quantity;
      }

      if (e.target.value > _.get(this, 'cartItem.product.maximumOrderQuantity', this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY)) {
        this.disabled = false // remove semiopaque overlay
        this.cartItemErrorMessage = this.$store.state.config.constants.ERROR_MAX_ORDER_QUANTITY // Quantity limit reached
        // reset value back to original
        e.target.value = this.cartItem.quantity;
        this.item.quantity = this.cartItem.quantity;
        return
      }

      this.checkQuantity;
    },
    checkQuantity(e) {
      var quantityEntered = parseInt(e.target.value.replace(/[^0-9.]/g, ""));

      if (this.item.quantity === quantityEntered) {
        return;
      }

      // Don't do anything yet. Might still be typing.
      if(quantityEntered < this.cartItem.product.minimumOrderQuantity){
        return
      }

      if (quantityEntered < 1) {
        this.toggleDelete();
        // reset value back to original
        e.target.value = this.cartItem.quantity;
        this.item.quantity = this.cartItem.quantity;
        return;
      }

      if (quantityEntered > this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY) {
        this.item.quantity = this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY
        e.target.value = this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY
        this.debouncedPutQuantity()
        return
      }

      this.item.quantity = quantityEntered
      e.target.value = quantityEntered
      this.debouncedPutQuantity()
    },
    increment: function () {
      this.cartItemErrorMessage = false

      if (!this.overrideAddToBucket) {
        if(this.item.quantity >= this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY) return;
        if(this.maximumOrderLimitReached){
          this.disabled = false // remove semiopaque overlay
          this.cartItemErrorMessage = this.$store.state.config.constants.ERROR_MAX_ORDER_QUANTITY // Quantity limit reached
          return
        }

        this.item.quantity++ // local copy of item
        this.debouncedPutQuantity()
      }
    },
    decrement: function () {
      this.cartItemErrorMessage = false

      if (this.item.quantity <= 1 || this.item.quantity <= this.cartItem.product.minimumOrderQuantity ) {
        this.toggleDelete()
        return
      }

      this.item.quantity-- // local copy of item
      this.debouncedPutQuantity()
    },
    clipCoupon (coupon) {
      if(!window.heb.data.user.isGuest){
        this.gtmClickCoupon('e_selectCoupon', coupon, this.position)
        this.$store.dispatch('cart/CLIP_COUPON', {couponId: coupon.couponId, callback: this.getCouponDetails(coupon)})
      }else{
        window.location = '/my-account/login';
      }
    },
    getCouponDetails: function(coupon) {
      var vm = this;
      vm.$store.dispatch("cart/FETCH_COUPON_DETAILS", {
        id: coupon.couponId,
        callback: vm.processCouponResponse,
        summary: true
      });
    },
    processCouponResponse(response) {
      if (response.errors) {
        this.errors = true;
        return;
      }

      this.coupon = response;
    }
  },
  mounted: function() {
    if (this.isSmallDisplay) {
      this.paddlesActive = false;
    } else {
      this.paddlesActive = true;
    }
  },
  created () {
    this.debouncedPutQuantity = _.debounce(function(){
      if(this.item.quantity > 0){
        this.pendingResponse = true
        this.cartItemErrorMessage = false

        this.$store.dispatch(this.bucket + '/PUT_ITEM_QUANTITY', this.item).then((response) => {
          // Reset back to previous state on error
          if(response.status !== 200){
            this.cartItemErrorMessage = this.getShortErrorMessage(response); // Inline Error Message
            this.disabled = false // remove semiopaque overlay

            this.item = Vue.util.extend({}, this.cartItem)
            this.pendingResponse = false
          }
        })

      }
    }, 500);
  },
  watch:{
    isSmallDisplay (newValue, oldValue){
      this.paddlesActive = !newValue
    },
    // debounce helper for quantity
    cartItem: {
      immediate: true,
      handler(newValue, oldValue) {
        // Multiple Vue instances for the same product can exist on a page (product card, cart item)
        if(!this.pendingResponse){
          // this specific vue instance isn't POSTing a change, so accept any changes from API
          this.item = Vue.util.extend({}, newValue)
        }else if(
          this.pendingResponse && (this.item.quantity === newValue.quantity)
        ){
          // this specific vue instance IS POSTing a change so don't update till this instance gets that change reflected in the API response
          this.item = Vue.util.extend({}, newValue)
          this.pendingResponse = false
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// the styles are down by at the lg breakpoint
// That many not be a good idea because there is cart on mobile and sparkle

@import "../../assets/lib";

// z-index

// 20 - mobile quantity
// 10 - disabled overlay
// 0 - Are you sure you want to delete modal
.btn-coupon {
  min-width: 184px;
  margin: 0 8px 8px 0;
}

.panel {
  background: #fff;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
  padding: 16px;
  margin-bottom: 8px;
}

.category-section {
  background: #fff;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
  padding: 0.25rem 0.75rem;
  position: relative;
  z-index: 0;
}

.cart-item__row {
  padding: 16px 0;
  border-bottom: 1px solid #ccc;
  position: relative;
  min-height: 6rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  // z-index: 0;
  background-color: #fff;
}

.cart-item__row:last-of-type {
  border-bottom: 0;
}

.cart-item__row-product {
  display: grid;
  grid-template-rows: auto auto auto auto;
  grid-template-columns: auto 48px 1fr auto;
  grid-gap: 4px;
  transition: all 0.125s ease-in-out;
}

.cart-item__delete {
  grid-column: 1;
  grid-row: 1 / 4;
  margin-right: 16px;
}

.cart-delete-item {
  opacity: 0.4;
  font-size: 1.75rem;
}

.cart-item__delete-confirm {
  text-align: center;
}

.cart-item__delete-confirm.isDeleting {
  position: absolute;
  // z-index: 50;
  background: #fff;
  left: 16px;
  z-index: 0;
}

.cart-item__delete-confirm-message {
  font-weight: 600;
  margin-bottom: 8px;
}

// Elements

.cart-item__thumbnail-container {
  grid-row: 1 / 3;
  grid-column: 2 / 3;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.cart-item__thumbnail {
  width: 100%;
  min-height: 48px;
}

.cart-item__display-name-and-note {
  grid-row: 1 / 3;
  grid-column: 3 / 4;
  font-size: 13px;
  flex-grow: 1;
  min-width: 0;
}

.cart-item__flag-best-available{
  position: relative;
  display: block;
}

.cart-item__display-name {
  overflow: hidden;
  display: block;
}

.cart-item__preference-and-note {
  display: flex;
  align-items: center;
}

.cart-item__preference {
  font-size: 12px;
  white-space: nowrap;
  margin: 8px 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cart-item__note-sub-mobile {
  grid-row: 3;
  grid-column: 3;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  font-size: 14px;
  margin-top: 6px;
}

.cart-item__substitution {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.cart-item__substitution-label {
  font-weight: 600;
  margin: 0;
}

.cart-item__substitution-checkbox {
  margin-right: 8px;
}

.cart-item__price-container {
  grid-row: 1;
  grid-column: 4;
  text-align: right;
  flex-shrink: 0;
  justify-self: flex-end;
  min-width: 64px;
}

.cart-item__total-price {
  font-weight: bold;
  position: relative;
  font-size: 14px;
}

.cart-item__total-price-disabled:after {
  width: 3.5rem;
  content: "";
  display: block;
  height: 2px;
  right: -4px;
  transform: rotate(-20deg);
  top: 40%;
  background: rgba(0, 0, 0, 0.3);
  position: absolute;
}

.cart-item__sale-price {
  color: $color-heb-primary;
  font-weight: bold;
}

.cart-item__each-price {
  font-size: 11px;
  color: #4a4a4a;
}

.cart-item__adjustments-toggle {
  display: none;
}

@media screen and (max-width: $breakpoint-tablet) {
  input.cart-item__input-quantity.notActive {
    border-radius: 4px !important;
  }
}

.cart-item__quantity {
  grid-row: 2;
  grid-column: 4;
  justify-content: flex-end;
  text-align: right;
  z-index: 20;
  position: relative;
  height: 40px;
  flex-shrink: 0;
}

.cart-item__row-product.disabled .paddle-dropshadow {
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
}

.cart-item__quantity-placeholder {
  position: absolute;
  right: 0;
}

.btn-decrement,
.btn-increment {
  height: 40px;
  width: 40px;
  border: 1px solid #ccc;
  text-align: center;
  padding: 0;
  display: inline-block;
  background-color: #fff;
  color: $color-text-standard;

  &:hover{
    background-color: #e6e6e6;
    color: $color-text-standard;
  }

  &.btn:focus,
  &.btn:active{
    color: $color-text-standard;
  }

  &.disabled {
    background-color: #ececec;
    color: #8b8b8b;
    cursor: default;
  }
}

.btn.cart-item__input-quantity {
  box-sizing: border-box;
  width: 3rem;
  height: 40px;
  line-height: 40px;
  border: 1px solid #ccc;
  padding: 0;
  -moz-user-select: text;
  -webkit-user-select: text;
  cursor: text;
  background-color: #ffffff;

  &:hover,
  &:active,
  &:focus {
    color: $color-text-standard;
    box-shadow: none;
  }
}

.cart-item__input-quantity.cart-item__estimated-weight {
  background-color: #fff;
  width: 4rem;
}

.cart-item__row-product.disabled .cart-item__row__disabled-overlay {
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.4);
}

.cart-item__row-product.isDeleting {
  transform: translateX(280px);
}

@media screen and (min-width: $breakpoint-desktop) {
  .fullcart-item {
    .btn-coupon {
      margin-left: 8px;
    }

    .cart-item__row-product {
      display: flex;
      align-items: center;
      flex-grow: 1;
      margin-bottom: 8px;
    }

    .cart-item__display-name {
      font-size: 16px;
      overflow: hidden;
      flex-grow: 1;
      flex-shrink: 1;
      text-align: left;
      max-height: 3rem;
    }

    .cart-item__preference {
      font-size: 14px;
      margin: 0 0 8px 0;
    }

    .cart-item__notes-section {
      font-size: 14px;
    }

    .cart-item__thumbnail-container {
      width: 64px;
      height: 64px;
      margin-right: 16px;
    }

    .cart-item__quantity-placeholder {
      position: relative;
      right: auto;
    }

    .cart-item__price-container {
      width: 90px;
    }

    .cart-item__quantity {
      margin: 0 16px;
    }

    .btn.cart-item__input-quantity {
      width: 3rem;
      border: 1px solid #ccc;
      padding: 0;
      cursor: text;

      &:hover,
      &:active,
      &:focus {
        color: $color-text-standard;
        background-color: none;
        cursor: text;
      }
    }

    .cart-item__substitution {
      margin: 0 16px 0 16px;
      align-items: center;
      white-space: nowrap;
    }

    .cart-total-section {
      width: 25%;
    }

    .cart-item__total-price {
      font-size: 1.125rem;
    }

    .cart-item__each-price {
      font-size: 12px;
    }

    .cart-item__bottom-bar {
      display: flex;
      justify-content: space-between;
    }

    .cart-item__adjustments-toggle-container {
      width: 16px;
      margin-right: 8px;
      flex-shrink: 0;
    }

    .cart-item__adjustments-toggle {
      display: block;
    }

    .cart-item__adjustments {
      color: $color-heb-red;
      width: 300px;
      text-align: right;
    }

    .cart-item__adjustment-circle {
      border: 1px solid rgb(51, 51, 51);
      border-radius: 16px;
      width: 16px;
      height: 16px;
      padding-left: 3px;
      line-height: 1;
    }

    .cart-item__adjustment-type {
      font-weight: normal;
      // display: inline-block;
    }

    .cart-item__adjustment-value {
      font-weight: bold;
      padding-left: 30px;
      // display: inline-block;
    }
  }

}

</style>